import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAuth } from '@clerk/react';
import { cleanBookTitle, useDiffitBooks } from '../../Homepage/Contexts/DiffitBooksContext';
import { extractUrlsFromText } from '../../Homepage/Utils/GenerationUtils';
import { Mixpanel } from '../../../services/Mixpanel';
import { SUBJECT_MAP } from '../../../utils/skills';
import { getSubjects, getLowestSupportedGradeLevel, getRoles } from '../../../utils/users';
import { DiffitUserContext } from '../../../contexts/DiffitUserContext';
import { useLandingPageVariant } from '../../../contexts/LandingPageVariantContext';
import { resolveUserPills } from '../../../utils/helperPills';
import { useLocalStorage } from '../../../services/useLocalStorage';
import { useUnlimitedModals } from './UnlimitedModalsContext';
import { useAttachments } from '../hooks/useAttachments';
import { useAutocomplete } from '../hooks/useAutocomplete';
import { useResourceCreation } from '../hooks/useResourceCreation';
import { useFollowUpFlow } from '../hooks/useFollowUpFlow';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { useSubmitFlow } from '../hooks/useSubmitFlow';
import { useInputUI } from '../hooks/useInputUI';
import { useInitEffects } from '../hooks/useInitEffects';
import { retireTeacherCurriculumAnnouncement } from '../../../ads/curriculumAnnouncementNux';
import { useDebugMode } from '../../../hooks/useDebugMode';

const COMPARE_EXPERIMENT_KEY = 'diffit_compare_experiment_id';
import { EXCLUSIVELY_NON_ELA } from '../constants';

const UnlimitedCtx = createContext(null);

/**
 * Returns the Unlimited context value. Must be used within an UnlimitedProvider.
 */
export const useUnlimitedContext = () => {
  const ctx = useContext(UnlimitedCtx);
  if (!ctx) throw new Error('useUnlimitedContext must be used within an UnlimitedProvider');
  return ctx;
};

/**
 * The bare provider, for a surface that offers the create screen's Attach / Adapt / Align
 * menus over its own state (the Courses chat input). It must supply every field those
 * menus read.
 */
export const UnlimitedContextValueProvider = UnlimitedCtx.Provider;

/**
 * Central state provider for the Unlimited workshop — owns all creation-flow state
 * and delegates logic to focused hooks:
 * - useAttachments — file/image/link state
 * - useResourceCreation — API creation + streaming
 * - useFollowUpFlow — Q&A state machine
 * - useSubmitFlow — submit validation + initial API call
 * - useAutocomplete — suggestions + pills
 * - useInputUI — textarea, placeholder, pill bounds
 * - useInitEffects — page init (LogRocket, params, welcome modal)
 */
export const UnlimitedProvider = ({ children }) => {
  const { findPossibleBookMatchForTopic } = useDiffitBooks();
  const { unlimitedInputCopyVariations, alignToCurriculum, extraCurricula } = useFlags();
  const { isSignedIn } = useAuth();
  // Attach / Adapt / Align only configure the generation request, and anonymous users
  // are allowed to run that generation under experimentFreeGenerate — so those menus
  // use `requireAuthForMenus`, which skips the sign-in gate once the flag is on and LD
  // has identified the visitor. Account-bound entry points (prompt history) keep the
  // strict `requireAuth`.
  const { requireAuth, requireAuthForMenus } = useRequireAuth();
  const modals = useUnlimitedModals();
  const { diffitUser, isLDidentified } = useContext(DiffitUserContext);
  const landingPageVariant = useLandingPageVariant();

  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  // Shared ref for the main textarea — created here so both useAttachments
  // (needs focus after YouTube transcript) and useInputUI (owns resize) can use it.
  const inputRef = useRef(null);

  // ── Attachments ──
  const attachments = useAttachments({ setError, setQuery, inputRef });

  // ── Book selection ──
  const [selectedBook, setSelectedBook] = useState(null);
  const [startChapter, setStartChapter] = useState(-1);
  const [endChapter, setEndChapter] = useState(-1);

  const showTopLevelBooks = useMemo(() => {
    // Only classroom teachers are asked for grades and subjects, so other roles skip that check.
    const roles = getRoles(diffitUser);
    if (roles.length > 0 && !roles.includes('classroom_teacher')) return true;
    const subjects = getSubjects(diffitUser);
    const lowestGrade = getLowestSupportedGradeLevel(diffitUser);
    const isELA = subjects.includes('English Language Arts');
    const isElementary = lowestGrade !== null && lowestGrade <= 5;
    const isExclusivelyNonELA =
      subjects.length > 0 && subjects.every((s) => EXCLUSIVELY_NON_ELA.includes(s));
    return isELA || (isElementary && !isExclusivelyNonELA);
  }, [diffitUser]);

  const clearBook = useCallback(() => {
    Mixpanel.track('Unlimited Book Removed');
    setSelectedBook(null);
    setStartChapter(-1);
    setEndChapter(-1);
  }, []);

  const handleBookSelected = useCallback(
    (book, start, end) => {
      setSelectedBook(book);
      setStartChapter(start);
      setEndChapter(end);
      modals.setShowBookModal(false);
      Mixpanel.track('Unlimited Book Selected', {
        book_title: book.title,
        book_author: book.author,
        start_chapter: start,
        end_chapter: end,
      });
    },
    [modals]
  );

  // ── Template selection ──
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [refetchTemplatesTrigger, setRefetchTemplatesTrigger] = useState(0);

  const clearTemplate = useCallback(() => {
    Mixpanel.track('Unlimited Template Removed');
    setSelectedTemplate(null);
  }, []);

  // ── Standards selection ──
  const [languageStandards, setLanguageStandards] = useState([]);
  const [contentStandards, setContentStandards] = useState([]);
  const [showStandardsSubmenu, setShowStandardsSubmenu] = useState(false);

  const hasSelectedStandards = languageStandards.length > 0 || contentStandards.length > 0;

  const handleStandardsSelected = useCallback(
    ({ selected }) => {
      const subjectKey = modals.standardsSubject;
      const subject = subjectKey ? SUBJECT_MAP[subjectKey] : null;
      const selectedArray = Array.isArray(selected) ? selected : selected ? [selected] : [];
      if (subject?.standardType === 'language') {
        setLanguageStandards(selectedArray);
      } else {
        setContentStandards(selectedArray);
      }
      Mixpanel.track('Unlimited Standards Selected', {
        subject: subjectKey,
        standards_count: selectedArray.length,
      });
      modals.setShowStandardsModal(false);
      modals.setStandardsSubject(null);
    },
    [modals]
  );

  // ── Curriculum alignment (flag-gated by align-to-curriculum) ──
  // { curriculumId, curriculumName, courseId, courseName, lessons: [...] } or null.
  const [curriculumSelection, setCurriculumSelection] = useState(null);

  const handleCurriculumSelected = useCallback(
    (selection) => {
      setCurriculumSelection(selection?.lessons?.length ? selection : null);
      // A teacher who has aligned to a curriculum needs no announcement telling them they can.
      if (selection?.lessons?.length) retireTeacherCurriculumAnnouncement(diffitUser);
      Mixpanel.track('Unlimited Curriculum Selected', {
        curriculum_id: selection?.curriculumId,
        curriculum_source: selection?.curriculumSource,
        course_id: selection?.courseId,
        lessons_count: selection?.lessons?.length || 0,
      });
      modals.setShowCurriculumModal(false);
    },
    [modals, diffitUser]
  );

  const openCurriculum = useCallback(
    (curriculumId) => {
      modals.setCurriculumId(curriculumId);
      modals.setShowCurriculumModal(true);
    },
    [modals]
  );

  // Flag off, the selection is inert everywhere (menu, payload, chips) even if a
  // session picked lessons before the flag flipped — same shape as selectedMtssTier.
  const effectiveCurriculumSelection = alignToCurriculum ? curriculumSelection : null;

  // Dropping the last lesson clears the whole selection — a course with no
  // lessons is not an alignment.
  const removeCurriculumLesson = useCallback((lessonId) => {
    setCurriculumSelection((prev) => {
      if (!prev) return prev;
      const lessons = prev.lessons.filter((lesson) => lesson.id !== lessonId);
      return lessons.length ? { ...prev, lessons } : null;
    });
  }, []);

  // ── Reading level & language ──
  const [selectedReadingLevels, setSelectedReadingLevels] = useLocalStorage(
    'diffit-unlimited-reading-level',
    [],
    (stored) => (stored && !Array.isArray(stored) ? [stored] : stored)
  );
  const [selectedLanguages, setSelectedLanguages] = useLocalStorage(
    'diffit-unlimited-language',
    [],
    (stored) => (stored && !Array.isArray(stored) ? [stored] : stored)
  );
  const totalAdaptSelections = selectedReadingLevels.length + selectedLanguages.length;
  const adaptAtCapacity = totalAdaptSelections >= 5;

  // ── Web sources mode ──
  // 'auto' (default — LLM decides), 'on' (force web_search), 'off' (no web tool).
  // Persisted in localStorage so a teacher's preference survives reloads.
  const [webSourcesMode, setWebSourcesModeRaw] = useLocalStorage(
    'diffit-unlimited-web-sources-mode',
    'auto',
    (stored) => (stored === 'on' || stored === 'off' || stored === 'auto' ? stored : 'auto')
  );
  const setWebSourcesMode = useCallback(
    (next) => {
      setWebSourcesModeRaw(next === 'on' || next === 'off' ? next : 'auto');
    },
    [setWebSourcesModeRaw]
  );

  // ── Pairwise compare (admin debug mode) ──
  // Experiment pick sticks for the browser session (sessionStorage), so a run of
  // comparisons doesn't depend on re-picking it every create. Ignored whenever debug
  // mode is off.
  const [debugMode] = useDebugMode();
  const [compareExperimentId, setCompareExperimentIdRaw] = useState(() => {
    try {
      return sessionStorage.getItem(COMPARE_EXPERIMENT_KEY) || null;
    } catch {
      return null;
    }
  });
  const setCompareExperimentId = useCallback((id) => {
    try {
      if (id) sessionStorage.setItem(COMPARE_EXPERIMENT_KEY, id);
      else sessionStorage.removeItem(COMPARE_EXPERIMENT_KEY);
    } catch {
      // Storage unavailable — the pick just won't outlive this page.
    }
    setCompareExperimentIdRaw(id);
  }, []);
  const effectiveCompareExperimentId = debugMode ? compareExperimentId : null;

  // ── MTSS tier ──
  // null = Automatic (default, no explicit choice). 'tier_1' = explicit Universal,
  // identical on the wire to null since the backend whitelists only 'tier_2' / 'tier_3'
  // as overlays — see effectiveMtssTier below. Session-scoped (useState, not localStorage)
  // so the tier choice never leaks across sessions — explicit tiers should be a deliberate
  // per-resource choice, not a sticky default.
  // UI value — what AdaptMenu reads to render the chip + active-row checkmark. Includes
  // 'tier_1' so the explicit Universal choice is visually distinct from Automatic.
  const [selectedMtssTier, setSelectedMtssTierState] = useState(null);
  const setSelectedMtssTier = useCallback((next) => {
    setSelectedMtssTierState(
      next === 'tier_1' || next === 'tier_2' || next === 'tier_3' ? next : null
    );
  }, []);
  // Wire value — what we actually send to the backend / suggestion API. 'tier_1' collapses
  // to null because the backend only acts on 'tier_2' / 'tier_3' (no-op overlay for tier_1).
  const effectiveMtssTier =
    selectedMtssTier === 'tier_2' || selectedMtssTier === 'tier_3' ? selectedMtssTier : null;

  // ── Original text mode ──
  // 'auto' (default — backend decides), 'on' (preserve the attached source text
  // verbatim), 'off' (never include the original as written). Session-scoped
  // (useState, not localStorage) — keeping or excluding the text should be a
  // deliberate per-resource choice, not a sticky default.
  const [originalTextMode, setOriginalTextModeState] = useState('auto');
  const setOriginalTextMode = useCallback((next) => {
    // Normalize: only allow 'auto' | 'on' | 'off'.
    setOriginalTextModeState(next === 'on' || next === 'off' ? next : 'auto');
  }, []);
  // A source the mode can apply to: PDF, link, book, or a large pasted text still
  // present (unedited) in the prompt. Owned here (not in AdaptMenu) because the
  // create payload reads originalTextMode whether or not the menu is mounted.
  const originalTextSourceAttached = !!(
    attachments.pdfFile ||
    attachments.attachedLinks?.length > 0 ||
    selectedBook ||
    (attachments.pastedSourceText && query.includes(attachments.pastedSourceText))
  );
  // Removing the source clears the setting — it's a per-document choice about a
  // specific text, so the mode must not linger (or be sent) once that text is gone.
  useEffect(() => {
    if (!originalTextSourceAttached) setOriginalTextModeState('auto');
  }, [originalTextSourceAttached]);

  // ── Toolbar menu state ──
  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const [showAdaptMenu, setShowAdaptMenu] = useState(false);
  const [adaptSubmenuMobileStyle, setAdaptSubmenuMobileStyle] = useState(null);

  // ── Shared context builder ──
  // Builds the common book/template/standards/attachment context shared by both
  // the autocomplete suggestion API and Mixpanel tracking. Each caller extends
  // this with its own domain-specific fields.
  const buildCommonContext = useCallback(() => {
    const ctx = {};
    if (selectedBook && startChapter !== -1) {
      ctx.book_title = cleanBookTitle(selectedBook.title);
      ctx.book_author = selectedBook.author;
    }
    if (attachments.pdfFile) ctx.pdf_file_name = attachments.pdfFile.name;
    if (selectedTemplate) {
      ctx.template_title = selectedTemplate.title;
      ctx.template_description = selectedTemplate.description;
    }
    if (languageStandards.length > 0 || contentStandards.length > 0) {
      ctx.has_standards = true;
      ctx.language_standards = languageStandards;
      ctx.content_standards = contentStandards;
    }
    return ctx;
  }, [
    selectedBook,
    startChapter,
    attachments.pdfFile,
    selectedTemplate,
    languageStandards,
    contentStandards,
  ]);

  // ── Suggestion context (for autocomplete API) ──
  const suggestionContext = useMemo(() => {
    const base = buildCommonContext();
    if (selectedBook && startChapter !== -1) {
      base.book_chapters = selectedBook.chapters
        .slice(startChapter, endChapter + 1)
        .map((ch) => (typeof ch === 'object' ? ch.title : ch));
    }
    // Campaign landing pages: suggestions assume the targeted audience.
    if (landingPageVariant?.name) base.landing_page = landingPageVariant.name;
    return base;
  }, [buildCommonContext, selectedBook, startChapter, endChapter, landingPageVariant]);

  // savedRequest is owned here to break the circular dep between useResourceCreation
  // (needs savedRequest) and useFollowUpFlow (needs handleCreate).
  const [savedRequest, setSavedRequest] = useState('');

  // ── Tracking context (for Mixpanel) ──
  // Extends buildCommonContext with tracking-specific fields (request text, chapter
  // indices, standard names, detected URLs, adapt selections).
  const getTrackingContext = useCallback(() => {
    const base = buildCommonContext();
    const request = (savedRequest || query || '').trim();
    const detectedUrls = request ? extractUrlsFromText(request) : [];
    return {
      ...base,
      ...(request && { request }),
      ...(selectedTemplate && { template_id: selectedTemplate.id }),
      ...(selectedBook && {
        ...(startChapter !== -1 && { book_start_chapter: startChapter + 1 }),
        ...(endChapter !== -1 && { book_end_chapter: endChapter + 1 }),
      }),
      ...(languageStandards.length > 0 && {
        language_standards_names: languageStandards.map((s) => s.name || s.id),
      }),
      ...(contentStandards.length > 0 && {
        content_standards_names: contentStandards.map((s) => s.name || s.id),
      }),
      ...((detectedUrls.length > 0 || attachments.attachedLinks.length > 0) && {
        detected_urls: [...new Set([...detectedUrls, ...attachments.attachedLinks])],
        url_count: new Set([...detectedUrls, ...attachments.attachedLinks]).size,
      }),
      ...(selectedReadingLevels.length > 0 && {
        reading_level: selectedReadingLevels[0].name,
        reading_levels: selectedReadingLevels.map((l) => l.name),
      }),
      ...(selectedLanguages.length > 0 && {
        language: selectedLanguages[0].name,
        languages: selectedLanguages.map((l) => l.name),
      }),
      ...(effectiveMtssTier && { mtss_tier: effectiveMtssTier }),
      ...(originalTextMode !== 'auto' && { original_text_mode: originalTextMode }),
    };
  }, [
    buildCommonContext,
    savedRequest,
    query,
    selectedTemplate,
    selectedBook,
    startChapter,
    endChapter,
    languageStandards,
    contentStandards,
    attachments.attachedLinks,
    selectedReadingLevels,
    selectedLanguages,
    effectiveMtssTier,
    originalTextMode,
  ]);

  // ── Resource creation ──
  const { isCreating, handleCreate } = useResourceCreation({
    getQuery: () => query,
    setError,
    selectedBook,
    startChapter,
    endChapter,
    selectedTemplate,
    languageStandards,
    contentStandards,
    selectedReadingLevels,
    selectedLanguages,
    webSourcesMode,
    compareExperimentId: effectiveCompareExperimentId,
    curriculumSelection: effectiveCurriculumSelection,
    selectedMtssTier: effectiveMtssTier,
    originalTextMode,
    pastedSourceText: attachments.pastedSourceText,
    imageAnalyses: attachments.imageAnalyses,
    sourceContent: attachments.sourceContent,
    getUnlimitedTrackingContext: getTrackingContext,
    savedRequest,
  });

  // ── Follow-up Q&A ──
  const followUp = useFollowUpFlow({
    selectedBook,
    startChapter,
    endChapter,
    languageStandards,
    contentStandards,
    curriculumSelection: effectiveCurriculumSelection,
    selectedMtssTier: effectiveMtssTier,
    imageAnalyses: attachments.imageAnalyses,
    sourceContent: attachments.sourceContent,
    setSourceContent: attachments.setSourceContent,
    setUploadedPdfURL: attachments.setUploadedPdfURL,
    setImageAnalyses: attachments.setImageAnalyses,
    getUnlimitedTrackingContext: getTrackingContext,
    handleCreate,
    savedRequest,
  });

  // ── Helper pills ──
  // Computed before useInputUI so the pill bounds ResizeObserver gets real data on mount.
  const userPills = useMemo(
    () => resolveUserPills({ isSignedIn, isLDidentified, landingPageVariant, diffitUser }),
    [diffitUser, isLDidentified, isSignedIn, landingPageVariant]
  );

  // ── Input UI ──
  const inputUI = useInputUI({
    query,
    attachments,
    selectedBook,
    selectedTemplate,
    hasSelectedStandards,
    userPills,
    inputRef,
  });

  const getDefaultRequest = useCallback(() => {
    if (selectedBook)
      return `Create a resource based on "${cleanBookTitle(selectedBook.title)}" by ${selectedBook.author}`;
    if (attachments.uploadedPdfURL && attachments.pdfFile)
      return `Create a resource from the uploaded PDF "${attachments.pdfFile.name}"`;
    if (attachments.uploadedImageURLs.length > 0)
      return attachments.uploadedImageURLs.length === 1
        ? 'Create a resource from the uploaded image'
        : `Create a resource from the ${attachments.uploadedImageURLs.length} uploaded images`;
    if (hasSelectedStandards) {
      const names = [...languageStandards, ...contentStandards]
        .slice(0, 3)
        .map((s) => s.name || s.id)
        .join(', ');
      return `Create a resource aligned to standards: ${names}`;
    }
    if (selectedTemplate) return `Create a resource using the "${selectedTemplate.title}" template`;
    return '';
  }, [
    selectedBook,
    attachments.uploadedPdfURL,
    attachments.pdfFile,
    attachments.uploadedImageURLs,
    hasSelectedStandards,
    languageStandards,
    contentStandards,
    selectedTemplate,
  ]);

  // ── Autocomplete ──
  // Defined before useSubmitFlow so setSelectedIndex is available.
  // Uses a stable ref for onSubmit to break the circular dependency
  // (useAutocomplete needs handleSubmit, useSubmitFlow needs setSelectedIndex).
  const handleSubmitRef = useRef(null);
  const stableOnSubmit = useCallback((...args) => handleSubmitRef.current?.(...args), []);
  const autocomplete = useAutocomplete({
    query,
    setQuery,
    suggestionContext,
    isSignedIn,
    inputRef: inputUI.inputRef,
    adjustTextareaHeight: inputUI.adjustTextareaHeight,
    setError,
    onSubmit: stableOnSubmit,
  });

  // ── Submit flow ──
  const { isLoadingQuestions, handleSubmit } = useSubmitFlow({
    query,
    setError,
    attachments,
    selectedBook,
    startChapter,
    endChapter,
    selectedTemplate,
    languageStandards,
    contentStandards,
    curriculumSelection: effectiveCurriculumSelection,
    selectedReadingLevels,
    selectedLanguages,
    selectedMtssTier: effectiveMtssTier,
    getTrackingContext,
    findPossibleBookMatchForTopic,
    isCreating,
    followUp,
    setSelectedIndex: autocomplete.setSelectedIndex,
    setSavedRequest,
    getDefaultRequest,
    handleCreate,
  });
  handleSubmitRef.current = handleSubmit;

  const isLoading = isCreating || isLoadingQuestions;

  const { clearPillCache } = autocomplete;
  useEffect(() => {
    clearPillCache();
  }, [clearPillCache, selectedBook, attachments.pdfFile, selectedTemplate]);

  // ── Prompt library ──
  const pendingTimers = useRef([]);
  useEffect(() => () => pendingTimers.current.forEach(clearTimeout), []);

  const handlePromptHistorySelect = useCallback(
    (prompt) => {
      setQuery(prompt);
      autocomplete.setSuggestions([]);
      autocomplete.setSelectedIndex(-1);
      const id = setTimeout(() => {
        pendingTimers.current = pendingTimers.current.filter((t) => t !== id);
        const el = inputUI.inputRef.current;
        if (el) {
          el.focus();
          el.selectionStart = el.selectionEnd = el.value.length;
        }
        autocomplete.fetchSuggestions(prompt);
      }, 0);
      pendingTimers.current.push(id);
    },
    [autocomplete, inputUI.inputRef]
  );

  const handlePromptLibrarySelect = useCallback(
    (template, source) => {
      const prompt = template.prompt.endsWith(' ') ? template.prompt : template.prompt + ' ';
      setQuery(prompt);
      autocomplete.setSuggestions([]);
      autocomplete.setSelectedIndex(-1);
      const focusAndFetch = (delay) => {
        const id = setTimeout(() => {
          pendingTimers.current = pendingTimers.current.filter((t) => t !== id);
          const el = inputUI.inputRef.current;
          if (el) {
            el.focus();
            el.selectionStart = el.selectionEnd = el.value.length;
          }
          autocomplete.fetchSuggestions(template.prompt);
        }, delay);
        pendingTimers.current.push(id);
      };
      if (source === 'inline') {
        inputUI.inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        focusAndFetch(600);
      } else if (source === 'placeholder') {
        // Accepted from the cycling placeholder via ArrowRight: the caret is
        // already in the box, so just park it after the prefix — no scrolling.
        focusAndFetch(0);
      }
      Mixpanel.track('Prompt Library Card Selected', {
        prompt: template.prompt,
        label: template.label,
        source,
      });
    },
    [autocomplete, inputUI.inputRef]
  );

  // ── Initialization effects ──
  useInitEffects({
    setQuery,
    autocomplete,
    adjustTextareaHeight: inputUI.adjustTextareaHeight,
    inputRef: inputUI.inputRef,
    setShowWelcomeModal: modals.setShowWelcomeModal,
    setSharedTemplateIds: modals.setSharedTemplateIds,
    setShowAdaptMenu,
    setShowPlusMenu,
    setShowStandardsSubmenu,
    openCurriculum,
    extraCurricula: alignToCurriculum ? extraCurricula : undefined,
  });

  // ── Context value ──
  // Grouped into domain sub-objects for readability. Components destructure the
  // group they need: const { input, book, attachments } = useUnlimitedContext();
  const value = useMemo(
    () => ({
      // Core input & submission state
      input: {
        query,
        setQuery,
        error,
        setError,
        inputRef: inputUI.inputRef,
        inputPlaceholder: inputUI.inputPlaceholder,
        isDefaultPlaceholder: inputUI.isDefaultPlaceholder,
        adjustTextareaHeight: inputUI.adjustTextareaHeight,
        hasInput:
          inputUI.hasInput ||
          query.trim().length > 0 ||
          attachments.uploadedPdfURL ||
          attachments.uploadedImageURLs.length > 0 ||
          selectedBook ||
          selectedTemplate ||
          hasSelectedStandards,
        isLoading,
        isCreating,
        isLoadingQuestions,
        handleSubmit,
      },
      // Auth gate helpers — `requireAuth` always gates; `requireAuthForMenus` lets
      // anonymous users through when experimentFreeGenerate is on.
      requireAuth,
      requireAuthForMenus,
      // Feature flags
      flags: { unlimitedInputCopyVariations, isLDidentified, alignToCurriculum },
      // Book selection
      book: {
        selectedBook,
        setSelectedBook,
        startChapter,
        endChapter,
        showTopLevelBooks,
        clearBook,
        handleBookSelected,
      },
      // Template selection
      template: {
        selectedTemplate,
        setSelectedTemplate,
        clearTemplate,
        refetchTemplatesTrigger,
        setRefetchTemplatesTrigger,
      },
      // Standards alignment
      standards: {
        languageStandards,
        setLanguageStandards,
        contentStandards,
        setContentStandards,
        hasSelectedStandards,
        showStandardsSubmenu,
        setShowStandardsSubmenu,
        handleStandardsSelected,
      },
      // Curriculum alignment (flag-gated by align-to-curriculum)
      curriculum: {
        selection: effectiveCurriculumSelection,
        selectedLessons: effectiveCurriculumSelection?.lessons || [],
        hasSelection: !!effectiveCurriculumSelection?.lessons?.length,
        handleCurriculumSelected,
        removeLesson: removeCurriculumLesson,
      },
      // Reading level & language adaptation
      adapt: {
        selectedReadingLevels,
        setSelectedReadingLevels,
        selectedLanguages,
        setSelectedLanguages,
        totalAdaptSelections,
        adaptAtCapacity,
        originalTextMode,
        setOriginalTextMode,
        originalTextSourceAttached,
      },
      // MTSS tier overlay
      mtss: {
        selectedMtssTier,
        setSelectedMtssTier,
      },
      // Web sources mode toggle (auto | on | off)
      webSources: {
        mode: webSourcesMode,
        setMode: setWebSourcesMode,
      },
      // Pairwise compare picker (admin debug mode)
      compare: {
        experimentId: effectiveCompareExperimentId,
        setExperimentId: setCompareExperimentId,
      },
      // Toolbar menu visibility
      toolbar: {
        showPlusMenu,
        setShowPlusMenu,
        showAdaptMenu,
        setShowAdaptMenu,
        adaptSubmenuMobileStyle,
        setAdaptSubmenuMobileStyle,
      },
      // File/image/link attachments (from useAttachments hook)
      attachments,
      // Follow-up Q&A flow
      followUp: {
        showFollowUp: followUp.showFollowUp,
        currentQuestion: followUp.currentQuestion,
        questionKey: followUp.questionKey,
        isLoadingNextQuestion: followUp.isLoadingNextQuestion,
        restoredSelections: followUp.restoredSelections,
        previousQA: followUp.previousQA,
        handleFollowUpAnswered: followUp.handleFollowUpAnswered,
        handleBookConfirmed: followUp.handleBookConfirmed,
        handleSourceContentProvided: followUp.handleSourceContentProvided,
        handleFollowUpBack: followUp.handleFollowUpBack,
        handleFollowUpSkip: followUp.handleFollowUpSkip,
      },
      // Autocomplete suggestions (from useAutocomplete hook)
      autocomplete,
      // Helper pills & positioning
      suggestions: { userPills, pillBounds: inputUI.pillBounds, pillsRef: inputUI.pillsRef },
      // Prompt library selection handler
      handlePromptLibrarySelect,
      // Prompt history selection handler (clock-icon dropdown)
      handlePromptHistorySelect,
    }),
    [
      query,
      error,
      isLoading,
      isCreating,
      isLoadingQuestions,
      handleSubmit,
      inputUI,
      requireAuth,
      requireAuthForMenus,
      unlimitedInputCopyVariations,
      selectedBook,
      startChapter,
      endChapter,
      showTopLevelBooks,
      clearBook,
      handleBookSelected,
      selectedTemplate,
      refetchTemplatesTrigger,
      clearTemplate,
      languageStandards,
      contentStandards,
      hasSelectedStandards,
      showStandardsSubmenu,
      handleStandardsSelected,
      alignToCurriculum,
      effectiveCurriculumSelection,
      handleCurriculumSelected,
      removeCurriculumLesson,
      selectedReadingLevels,
      selectedLanguages,
      totalAdaptSelections,
      adaptAtCapacity,
      originalTextMode,
      setOriginalTextMode,
      originalTextSourceAttached,
      selectedMtssTier,
      setSelectedMtssTier,
      webSourcesMode,
      setWebSourcesMode,
      effectiveCompareExperimentId,
      showPlusMenu,
      showAdaptMenu,
      adaptSubmenuMobileStyle,
      attachments,
      followUp,
      autocomplete,
      userPills,
      isLDidentified,
      handlePromptLibrarySelect,
      handlePromptHistorySelect,
    ]
  );

  return <UnlimitedCtx.Provider value={value}>{children}</UnlimitedCtx.Provider>;
};
